import Konva from "konva";
import React from "react";
import { Group, Text } from "react-konva";
import { TextShape as ITextShape } from "../../../../types";
import { mergeRefs } from "../../../../utils/mergeRef";
import { DEFAULT_FONT_FAMILY } from "../../defines";
import { getTextProps } from "../../helpers/getTextProps";
import { ShapeCommonProps } from "../../types";
import { useMapEditorActions } from "../../contexts/MapEditorContext";
import { useShape } from "../ShapeContext/useShape";
import { TextEditor } from "./TextShapeEditor";
import { FiberProvider } from "@gettactic/helpers/src/dom/useContextBridge";

export const TextShape = React.forwardRef<
  React.ComponentRef<typeof Text>,
  ShapeCommonProps
>((props, ref) => {
  const { isHovered, shape, isInSelection, onUpdate, isGroupChild, disabled } =
    useShape();
  const { disableHistoryRecording, enableHistoryRecording } = useMapEditorActions();

  const { highlightShape, highlightShapeData, ...commonProps } = props;

  const textShape = shape as ITextShape;
  const shapeRef = React.useRef<React.ComponentRef<typeof Text>>(null);

  //const [editorEnabled, setEditorEnabled] = React.useState(false);
  const textRef = React.useRef<Konva.Text>(null);

  const editorEnabled = !!highlightShapeData;

  React.useEffect(() => {
    if (editorEnabled) {
      disableHistoryRecording();
    }
    return () => {
      if (editorEnabled) {
        enableHistoryRecording();
      }
    };
  }, [editorEnabled, disableHistoryRecording, enableHistoryRecording]);

  const text = textShape.text;

  const onChange = (newText: string) => {
    onUpdate({ text: newText });
  };

  // Grow the shape's stored height to fit the wrapped text. Reading the
  // height from the in-tree Konva node was unreliable (textArr can be stale
  // when the node is hidden during inline editing). Constructing a transient
  // Konva.Text with the same wrapping props gives us the natural auto-height
  // synchronously, regardless of what the rendered node is doing.
  // Manual resize handles still work — we only ever grow, never shrink.
  React.useLayoutEffect(() => {
    const fontStyle =
      textShape.isBold && textShape.isItalic
        ? "italic bold"
        : textShape.isBold
          ? "bold"
          : textShape.isItalic
            ? "italic"
            : "normal";
    const measure = new Konva.Text({
      text: textShape.text ?? "",
      fontSize: textShape.fontSize || 20,
      fontFamily: textShape.fontFamily || DEFAULT_FONT_FAMILY,
      fontStyle,
      width: textShape.width,
      padding: 0,
      align: textShape.textAlign || undefined
    });
    const naturalHeight = Math.ceil(measure.height());
    measure.destroy();

    if (naturalHeight > textShape.height + 1) {
      onUpdate({ height: naturalHeight });
    }
  }, [
    textShape.text,
    textShape.fontSize,
    textShape.fontFamily,
    textShape.width,
    textShape.isBold,
    textShape.isItalic,
    textShape.textAlign
  ]);

  const handleTextDblClick = () => {
    if (textShape.type === "text" && !isGroupChild && !shape.locked) {
      highlightShape?.({
        shape: textShape,
        targetId: textShape.id
      });
    }
  };

  const textProps = {
    x: editorEnabled ? 0 : commonProps.x,
    y: editorEnabled ? 0 : commonProps.y,
    text,
    fontSize: textShape.fontSize,
    fontFamily: textShape.fontFamily,
    width: textShape.width,
    height: textShape.height,
    stroke:
      !textShape.locked && (isHovered || isInSelection)
        ? "#000000"
        : "transparent",
    strokeWidth: 0.5,
    opacity: textShape.locked ? 0.5 : 1,
    onDblClick: handleTextDblClick,
    visible: !editorEnabled
  };

  if (!editorEnabled) {
    return (
      <Text
        {...commonProps}
        {...textProps}
        ref={mergeRefs([shapeRef, ref, textRef])}
        {...getTextProps(textShape)}
        visible={!editorEnabled}
      />
    );
  }

  return (
    <FiberProvider>
      <Group x={commonProps.x} y={commonProps.y}>
        <Text
          {...commonProps}
          {...textProps}
          ref={mergeRefs([shapeRef, ref, textRef])}
          {...getTextProps(textShape)}
          visible={!editorEnabled}
        />
        {editorEnabled && (
          <Group>
            <TextEditor
              value={text}
              textNodeRef={textRef}
              onChange={onChange}
              onBlur={() => {
                highlightShape?.(null);
                commonProps.onMouseLeave({} as never);
              }}
              onMouseLeave={commonProps.onMouseLeave}
            />
          </Group>
        )}
      </Group>
    </FiberProvider>
  );
});
